import { useEffect, useState } from 'react';
import { Box, Text, Spinner, Alert, AlertIcon, VStack, HStack, Input, useColorModeValue, Center, Circle } from '@chakra-ui/react';
import { Link } from 'react-router-dom';

// Grade calculation helper functions - kept for potential future use
// const calculateGrade = (count) => {
//   if (count === 0) return { grade: 'A', fill: 100 };
//   if (count <= 2) return { grade: 'B', fill: 80 };
//   if (count <= 5) return { grade: 'C', fill: 50 };
//   if (count <= 10) return { grade: 'D', fill: 20 };
//   return { grade: 'F', fill: 0 };
// };

const GradeIndicator = ({ grade, label }) => {
  const boxBg = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const circleBg = useColorModeValue('gray.300', 'gray.600');
  
  // Define colors for each grade
  const gradeAColor = useColorModeValue('green.500', 'green.400');
  const gradeBColor = useColorModeValue('yellow.500', 'yellow.400');
  const gradeCColor = useColorModeValue('orange.500', 'orange.400');
  const gradeDColor = useColorModeValue('red.500', 'red.400');
  const gradeEColor = useColorModeValue('orange.600', 'orange.500'); // Dark orange for E
  const gradeFColor = useColorModeValue('red.500', 'red.400'); // Red for F
  const defaultColor = useColorModeValue('gray.500', 'gray.400');
  
  // Get color based on grade
  const getGradeColor = (grade) => {
    switch (grade.toUpperCase()) {
      case 'A': return gradeAColor;
      case 'B': return gradeBColor;
      case 'C': return gradeCColor;
      case 'D': return gradeDColor;
      case 'E': return gradeEColor;
      case 'F': return gradeFColor;
      default: return defaultColor;
    }
  };
  
  const gradeColor = getGradeColor(grade);
  const isFGrade = grade.toUpperCase() === 'F';
  
  return (
    <Box
      bg={boxBg}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      px={2}
      py={1}
      minW="90px"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
    >
      <Text fontSize="xs" color={textColor} fontWeight="bold" letterSpacing="wider" mr={2}>
        {label.toUpperCase()}
      </Text>
      <Circle 
        size="24px" 
        bg={isFGrade ? circleBg : gradeColor}
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="none"
      >
        <Text
          fontSize="xs"
          fontWeight="bold"
          color={isFGrade ? textColor : 'white'}
          textShadow={isFGrade ? 'none' : '2px 2px 4px rgba(0,0,0,0.9)'}
        >
          {grade}
        </Text>
      </Circle>
    </Box>
  );
};

export default function Dashboard() {
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const tableBg = useColorModeValue('white', 'gray.800');
  const tableText = useColorModeValue('black', 'white');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const boxBg = useColorModeValue('gray.50', 'gray.900');

  console.log('Dashboard component rendering, repos:', repos.length);

  useEffect(() => {
    console.log('Dashboard useEffect running, fetching repos...');
    fetch('/api/repos')
      .then((res) => {
        console.log('API response status:', res.status);
        if (!res.ok) throw new Error('Failed to fetch repos');
        return res.json();
      })
      .then((data) => {
        console.log('API data received:', data.length, 'repos');
        setRepos(data);
      })
      .catch((err) => {
        console.error('API error:', err);
        setError(err.message);
      })
      .finally(() => {
        console.log('API call finished, setting loading to false');
        setLoading(false);
      });
  }, []);

  const filteredRepos = repos.filter(repo => {
    const name = repo.title || repo.repository || '';
    return name.toLowerCase().includes(search.toLowerCase());
  });

  console.log('Dashboard render - loading:', loading, 'error:', error, 'repos:', repos.length);
  
  if (loading) {
    console.log('Showing loading spinner');
    return <Center minH="60vh"><Spinner size="xl" /></Center>;
  }
  if (error) {
    console.log('Showing error:', error);
    return <Alert status="error" mt={10}><AlertIcon />Error: {error}</Alert>;
  }

  console.log('Rendering main dashboard content');
  
  return (
    <Box maxW="800px" mx="auto" mt={10} p={4} bg={boxBg} borderRadius="md">
      <Text fontSize="2xl" mb={4}>Repository Dashboard</Text>
      <Text mb={4}>Found {repos.length} repositories</Text>
      <Input
        placeholder="Search repositories..."
        mb={6}
        value={search}
        onChange={e => setSearch(e.target.value)}
        bg={tableBg}
      />
      <VStack spacing={4} align="stretch">
        {filteredRepos.map((repo, idx) => {

          return (
            <Box
              key={idx}
              p={6}
              bg={tableBg}
              color={tableText}
              borderRadius="md"
              boxShadow="md"
              _hover={{ bg: hoverBg }}
              as={Link}
              to={`/repo/${repo.repository}`}
              style={{ textDecoration: 'none' }}
            >
              <HStack spacing={4} align="center" justify="space-between">
                {/* Repo icon, title, description */}
                <HStack spacing={4} align="center">
                  <img
                    src={`/api/icon/${repo.id || 'default'}`}
                    alt="icon"
                    style={{ width: 40, height: 40, borderRadius: 6 }}
                    onError={e => { e.target.src = '/api/icon/default'; }}
                  />
                  <Box>
                    <Text fontSize="lg" fontWeight="bold" mb={0}>
                      {repo.title}
                    </Text>
                    <Text fontSize="sm" color="gray.500" noOfLines={2}>
                      {repo.manifest?.description || 'No description available.'}
                    </Text>
                  </Box>
                </HStack>
                {/* Grade boxes on the right */}
                <HStack spacing={2} align="center">
                  <GradeIndicator grade={repo.grades?.sast || 'A'} label="SAST" />
                  <GradeIndicator grade={repo.grades?.license || 'A'} label="LICENSE" />
                  <GradeIndicator grade={repo.grades?.cve || 'A'} label="CVE" />
                </HStack>
              </HStack>
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
} 